import { useEffect, useRef, useState, type ReactNode } from 'react';

import { isRTL } from '@skyscanner/backpack-web/bpk-react-utils';

import STYLES from './MobileScrollContainer.module.scss';

export type MobileScrollContainerProps = {
  children: ReactNode[];
  onScrollComplete?: (args: number) => void;
  preventComplete?: boolean;
  selectedItem?: number;
  setVisibleIndex?: (args: number) => void;
};

export default function MobileScrollContainer({
  children,
  onScrollComplete = undefined,
  preventComplete = false,
  selectedItem = 0,
  setVisibleIndex = undefined,
}: MobileScrollContainerProps) {
  const [internalIndex, setInternalIndex] = useState(selectedItem);
  const scrollContainer = useRef<HTMLDivElement>(null);

  // When the selected item changes, scroll to the new item
  useEffect(() => {
    if (!scrollContainer.current) return;
    const container = scrollContainer.current;

    const itemWidth = container.firstElementChild?.clientWidth;
    if (!itemWidth) return;

    // Calculate the new left scroll position based on the selected item
    const left = isRTL()
      ? -(selectedItem * itemWidth)
      : selectedItem * itemWidth;

    // Scroll to the selected item
    scrollContainer.current.scrollTo({
      left,
      behavior: 'smooth',
    });
  }, [selectedItem]);

  useEffect(() => {
    if (!scrollContainer.current) return;
    const container = scrollContainer.current;

    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;

      const itemWidth = target.firstElementChild?.clientWidth;
      if (!itemWidth) return;

      // Calculate the index of the visible item
      const index = Math.round(Math.abs(target.scrollLeft) / itemWidth);

      // Update the internal index state when the visible index changes
      if (index !== internalIndex) {
        setInternalIndex(index);
        setVisibleIndex?.(index);
      }

      // Check if the scroll position aligns with the start of the card
      const cardPosition = index * itemWidth;
      const aligns = Math.abs(target.scrollLeft) === cardPosition;

      /**
       *  Check if the scroll should complete
       *  If preventComplete is false, the scroll should complete when the scroll aligns with the start of the card
       *  This is to prevent the scroll from completing when the user has clicked a marker on the map until the scroll aligns with the start of the selected card
       */
      const shouldComplete = !preventComplete || index === selectedItem;

      if (aligns && shouldComplete) {
        onScrollComplete?.(index);
      }
    };

    container.addEventListener('scroll', handleScroll);

    // eslint-disable-next-line consistent-return
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [
    internalIndex,
    onScrollComplete,
    preventComplete,
    selectedItem,
    setVisibleIndex,
  ]);

  return (
    <div
      ref={scrollContainer}
      className={STYLES.MobileScrollContainer}
      data-testid="mobile-scroll-container"
    >
      {children}
    </div>
  );
}
